import { getPlacementSummary } from './placementUtils';
import { DEFAULT_PIPELINE, getPipelineStages } from './programPipeline';

export interface ApplicantFilterState {
  workflowPhase: string[];      // Workflow Phase: draft, submitted, screening, processing, billings, immigration, preDeparture, closed
  sponsor: string[];            // Sponsor / Partner Assigned
  gender: string[];             // Gender
  country: string[];            // Country of citizenship / birth
  financialStation: string[];   // Financial Station: Outstanding Invoice, Full Paid, No Invoice, Partially Paid, Waived
  status: string[];             // Application Status: Draft, Submitted, Returned, Cancelled, Closed, Archived
  masterPhase: string[];        // Master Phase: pipeline stage indices as strings ('0'..), labels sourced from program pipelines (ONLY in Batches!)
  tags: string[];               // Application tags
  placementStatus: string[];    // Placement/Placed Status: Placed, Pending Approval, Pending Placement, Cancelled
  createdFrom: string;          // Application Creation Date From
  createdTo: string;            // Application Creation Date To
  submittedFrom: string;        // Application Submitted Date From
  submittedTo: string;          // Application Submitted Date To
  program: string[];            // Program
  pricingVariable: string[];    // Program Pricing Variable
  term: string[];               // Term
  batches: string[];            // Batches (Applicants can belong to 1 or more batches)
  referrer: string[];           // Referrer Assigned
  assignedTeamMember: string[]; // Assigned Team Member
}

export const DEFAULT_FILTER_STATE: ApplicantFilterState = {
  workflowPhase: [],
  sponsor: [],
  gender: [],
  country: [],
  financialStation: [],
  status: [],
  masterPhase: [],
  tags: [],
  placementStatus: [],
  createdFrom: '',
  createdTo: '',
  submittedFrom: '',
  submittedTo: '',
  program: [],
  pricingVariable: [],
  term: [],
  batches: [],
  referrer: [],
  assignedTeamMember: [],
};

/**
 * One key per filter criterion the bar can offer. Screens whose record
 * shape cannot feed a criterion hide it (per-screen gating): a hidden
 * criterion renders no control, contributes nothing to the active-filter
 * accounting, and — via excludeFilterCriteria — is never applied by the
 * engine call on that screen.
 */
export type FilterCriterionKey =
  | 'workflowPhase'
  | 'sponsor'
  | 'gender'
  | 'country'
  | 'financialStation'
  | 'status'
  | 'masterPhase'
  | 'tags'
  | 'placementStatus'
  | 'createdRange'
  | 'submittedRange'
  | 'program'
  | 'pricingVariable'
  | 'term'
  | 'batches'
  | 'referrer'
  | 'assignedTeamMember';

export interface FilterActivityOptions {
  includeMasterPhase?: boolean;
  hiddenCriteria?: FilterCriterionKey[];
}

/** Array-valued state field per array criterion (ranges handled apart). */
const ARRAY_FIELD_BY_CRITERION: Partial<Record<FilterCriterionKey, keyof ApplicantFilterState>> = {
  workflowPhase: 'workflowPhase',
  sponsor: 'sponsor',
  gender: 'gender',
  country: 'country',
  financialStation: 'financialStation',
  status: 'status',
  masterPhase: 'masterPhase',
  tags: 'tags',
  placementStatus: 'placementStatus',
  program: 'program',
  pricingVariable: 'pricingVariable',
  term: 'term',
  batches: 'batches',
  referrer: 'referrer',
  assignedTeamMember: 'assignedTeamMember',
};

const ALL_CRITERIA = Object.keys({
  workflowPhase: 1, sponsor: 1, gender: 1, country: 1, financialStation: 1,
  status: 1, masterPhase: 1, tags: 1, placementStatus: 1, createdRange: 1,
  submittedRange: 1, program: 1, pricingVariable: 1, term: 1, batches: 1, referrer: 1,
  assignedTeamMember: 1,
}) as FilterCriterionKey[];

function criterionIsActive(filters: ApplicantFilterState, key: FilterCriterionKey): boolean {
  if (key === 'createdRange') return Boolean(filters.createdFrom || filters.createdTo);
  if (key === 'submittedRange') return Boolean(filters.submittedFrom || filters.submittedTo);
  const field = ARRAY_FIELD_BY_CRITERION[key];
  if (!field) return false;
  const value = filters[field] as string[] | undefined;
  return Boolean(value && value.length > 0);
}

export function hasActiveFilters(filters: ApplicantFilterState, options?: FilterActivityOptions): boolean {
  if (!filters) return false;
  const hidden = new Set(options?.hiddenCriteria || []);
  return ALL_CRITERIA.some((key) => {
    if (hidden.has(key)) return false;
    // Master Phase only exists where the screen opts in (Batches).
    if (key === 'masterPhase' && !options?.includeMasterPhase) return false;
    return criterionIsActive(filters, key);
  });
}

export function getActiveFilterCount(filters: ApplicantFilterState, options?: FilterActivityOptions): number {
  if (!filters) return 0;
  const hidden = new Set(options?.hiddenCriteria || []);
  let count = 0;
  for (const key of ALL_CRITERIA) {
    if (hidden.has(key)) continue;
    if (key === 'masterPhase' && !options?.includeMasterPhase) continue;
    if (key === 'createdRange' || key === 'submittedRange') {
      if (criterionIsActive(filters, key)) count += 1;
      continue;
    }
    const field = ARRAY_FIELD_BY_CRITERION[key];
    if (field) count += (filters[field] as string[] | undefined)?.length || 0;
  }
  return count;
}

/**
 * A copy of the filter state with the given criteria reset to their
 * defaults. A screen passes its hidden criteria through this before
 * handing the state to filterApplicantRecord, so a gated criterion can
 * never filter records on a screen whose data cannot feed it — even if
 * a stale value somehow reached the state.
 */
export function excludeFilterCriteria(
  filters: ApplicantFilterState,
  hidden: FilterCriterionKey[] = []
): ApplicantFilterState {
  if (!filters || !hidden || hidden.length === 0) return filters;
  const next: ApplicantFilterState = { ...filters };
  for (const key of hidden) {
    if (key === 'createdRange') {
      next.createdFrom = '';
      next.createdTo = '';
    } else if (key === 'submittedRange') {
      next.submittedFrom = '';
      next.submittedTo = '';
    } else {
      const field = ARRAY_FIELD_BY_CRITERION[key];
      if (field) (next as any)[field] = [];
    }
  }
  return next;
}

/**
 * Master Phase filter options, sourced from the portal's real pipeline
 * definitions — never a hardcoded label list. The option set covers the
 * UNION of stage indices across every program's pipeline plus the
 * default pipeline, so every masterStage index a record can carry is
 * selectable (the default pipeline alone has 10 stages, indices 0-9).
 *
 * Labels: a single index-keyed list cannot show two labels for one
 * index when programs customise their pipelines differently, so the
 * label is the DEFAULT pipeline's stage name for that index — the
 * platform-canonical meaning of the numeric masterStage index (see
 * programPipeline.ts: unconfigured programs use the default verbatim,
 * and the index stays meaningful against it). Indices beyond the
 * default pipeline's range take their label from the custom pipeline
 * that defines them.
 */
export function buildMasterPhaseOptions(programsList: any[] = []): { value: string; label: string }[] {
  const pipelines = (Array.isArray(programsList) ? programsList : []).map((p) => getPipelineStages(p));
  let maxLen = DEFAULT_PIPELINE.length;
  for (const stages of pipelines) maxLen = Math.max(maxLen, stages.length);
  const options: { value: string; label: string }[] = [];
  for (let i = 0; i < maxLen; i++) {
    const label =
      DEFAULT_PIPELINE[i]?.label ||
      pipelines.map((stages) => stages[i]).find(Boolean)?.label ||
      `Stage ${i + 1}`;
    options.push({ value: String(i), label });
  }
  return options;
}

/** Formatters enforcing Reference Number / Code FIRST Always */

export function formatBatchOption(b: any): { value: string; label: string } {
  if (!b) return { value: '', label: '' };
  const rawId = b.batchId || b.id || (b.numericId ? `BAT-${b.numericId}` : '');
  // Mirror the portal's D-5 batchPublicLabel: the display id is the
  // BAT-prefixed batchId, else `BAT-${numericId}`, else the raw batchId.
  const refCode = b.batchId?.startsWith('BAT-')
    ? b.batchId
    : b.numericId
    ? `BAT-${b.numericId}`
    : rawId;
  const name = b.name || b.title || '';
  const label = refCode && name && !name.includes(String(refCode))
    ? `${refCode} - ${name}`
    : name || refCode || 'Unnamed Batch';
  return { value: String(b.batchId || b.id || refCode), label };
}

export function formatProgramOption(p: any): { value: string; label: string } {
  if (!p) return { value: '', label: '' };
  const code = p.code || p.shortCode || p.programCode;
  const name = p.name || p.title || p.programName || p.id;
  const val = String(p.id || p.programId || code || name);
  const label = code && name && code !== name ? `${code} — ${name}` : name || code;
  return { value: val, label };
}

export function formatReferrerOption(r: any): { value: string; label: string } {
  if (!r) return { value: '', label: '' };
  // Established display id (PendingApproval): referrerId, else
  // `REF-${numericId}`, else the doc id — never a bare number.
  const id = r.referrerId || r.customId || r.code || (r.numericId ? `REF-${r.numericId}` : '') || r.id || r.uid;
  const refCode = id ? String(id) : '';
  const name = r.name || r.companyName || r.displayName || [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || '';
  const label = refCode && name && !name.includes(String(refCode))
    ? `${refCode} - ${name}`
    : name || refCode || 'Unnamed Referrer';
  return { value: String(r.id || r.referrerId || r.uid || refCode), label };
}

export function formatSponsorOption(s: any): { value: string; label: string } {
  if (!s) return { value: '', label: '' };
  // Established display id (SponsorsManager): sponsorId, else
  // `SPO-${numericId}`, else the doc id — never a bare number.
  const id = s.sponsorId || s.customId || s.code || (s.numericId ? `SPO-${s.numericId}` : '') || s.id || s.uid;
  const refCode = id ? String(id) : '';
  const name = s.name || s.companyName || s.organizationName || s.displayName || s.email || '';
  const label = refCode && name && !name.includes(String(refCode))
    ? `${refCode} - ${name}`
    : name || refCode || 'Unnamed Sponsor';
  // The option VALUE must be the identifier the application records carry:
  // applications stamp `assignedPartner` with the ServiceProvider's
  // providerId, so providerId leads the value chain (it is not a public
  // code, so it stays out of the label chain above).
  return { value: String(s.providerId || s.id || s.sponsorId || s.uid || name || refCode), label };
}

/** Filter Evaluation Logic for Applicant Records */
export function filterApplicantRecord(
  app: any,
  filters: ApplicantFilterState,
  options?: {
    includeMasterPhase?: boolean;
    batchesList?: any[];
    programsList?: any[];
    sponsorsList?: any[];
    referrersList?: any[];
  }
): boolean {
  if (!app) return false;
  if (!filters) return true;

  // 1. Workflow Phase
  if (filters.workflowPhase && filters.workflowPhase.length > 0) {
    // Normalise stage keys/labels to one comparable form: case-folded,
    // separators stripped ('pre_departure' = 'preDeparture',
    // 'ready for screening' still contains 'screening'), and the
    // canonical label 'Closure' aliased to the stage key 'closed'.
    const normStage = (v: any): string => {
      if (typeof v !== 'string') return '';
      const s = v.toLowerCase().replace(/[\s_]+/g, '');
      return s === 'closure' ? 'closed' : s;
    };
    const rawStage = normStage(app.stage || app.workflowStage || app.workflowPhase || app.phase || app.status || '');
    // A record with no stage value must never match a phase selection
    // (the old bidirectional includes() matched everything when the
    // record side was the empty string).
    if (!rawStage) return false;
    const match = filters.workflowPhase.some(p => {
      const pNorm = normStage(p);
      return pNorm !== '' && (rawStage === pNorm || rawStage.includes(pNorm));
    });
    if (!match) return false;
  }

  // 2. Sponsor / Partner Assigned
  if (filters.sponsor && filters.sponsor.length > 0) {
    // Exact, case-insensitive equality against every sponsor-shaped
    // field the record carries (the pre-unification dashboard filter
    // exact-matched selected providerIds against `assignedPartner`).
    // A record with no sponsor value never matches a sponsor selection.
    const candidates = [app.assignedPartner, app.sponsorId, app.sponsorName, app.sponsor, app.serviceProvider]
      .filter((v: any) => (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== '')
      .map((v: any) => String(v).trim().toLowerCase());
    if (candidates.length === 0) return false;
    const match = filters.sponsor.some(s => candidates.includes(String(s).trim().toLowerCase()));
    if (!match) return false;
  }

  // 3. Gender
  if (filters.gender && filters.gender.length > 0) {
    const appGender = (app.personalDetails?.gender || app.gender || '').trim();
    if (!filters.gender.some(g => g.toLowerCase() === appGender.toLowerCase())) {
      return false;
    }
  }

  // 4. Country
  if (filters.country && filters.country.length > 0) {
    const appCountry = (app.personalDetails?.countryOfCitizenship || app.personalDetails?.countryOfBirth || app.personalDetails?.country || app.country || '').toLowerCase().trim();
    if (!filters.country.some(c => c.toLowerCase().trim() === appCountry)) {
      return false;
    }
  }

  // 5. Financial Station
  if (filters.financialStation && filters.financialStation.length > 0) {
    const appFinStatus = app.financialStatus || app.billingStatus || app.paymentStatus || '';
    let derivedStation = 'No Invoice';
    if (appFinStatus === 'Paid' || app.balanceDue === 0) {
      derivedStation = 'Full Paid';
    } else if (appFinStatus === 'Outstanding Invoice' || appFinStatus === 'Unpaid' || appFinStatus === 'Overdue' || (app.balanceDue && app.balanceDue > 0)) {
      derivedStation = 'Outstanding Invoice';
    } else if (appFinStatus === 'Partial' || appFinStatus === 'Partially Paid') {
      // Stored application vocabulary is 'Partial' (the pre-unification
      // dashboard filter's exact option); the station label is
      // 'Partially Paid'. Map between them or Partial records match
      // nothing.
      derivedStation = 'Partially Paid';
    } else if (appFinStatus === 'No Invoice' || appFinStatus === '') {
      derivedStation = 'No Invoice';
    } else {
      derivedStation = appFinStatus;
    }

    const match = filters.financialStation.some(fs => {
      const fsLower = fs.toLowerCase();
      return derivedStation.toLowerCase() === fsLower || appFinStatus.toLowerCase() === fsLower;
    });
    if (!match) return false;
  }

  // 6. Application Status
  if (filters.status && filters.status.length > 0) {
    const appStatus = (app.submissionStatus || app.status || app.state || '').trim();
    if (!filters.status.some(st => st.toLowerCase() === appStatus.toLowerCase())) {
      return false;
    }
  }

  // 7. Master Phase (ONLY when includeMasterPhase is true)
  if (options?.includeMasterPhase && filters.masterPhase && filters.masterPhase.length > 0) {
    const appMaster = String(app.masterStage !== undefined ? app.masterStage : (app.masterPhase !== undefined ? app.masterPhase : ''));
    const match = filters.masterPhase.some(mp => {
      const mpStr = String(mp).replace(/^Phase\s*/i, '').trim();
      return appMaster === mpStr || appMaster === String(mp) || `phase ${appMaster}` === mp.toLowerCase();
    });
    if (!match) return false;
  }

  // 8. Application Tags
  if (filters.tags && filters.tags.length > 0) {
    const appTags: string[] = Array.isArray(app.tags) ? app.tags : [];
    const hasTagMatch = filters.tags.some(t => appTags.includes(t));
    if (!hasTagMatch) return false;
  }

  // 9. Placement / Placed Status
  if (filters.placementStatus && filters.placementStatus.length > 0) {
    const summary = getPlacementSummary({
      placements: app.placements,
      placementStatus: app.placementStatus,
      selfPlacement: app.selfPlacement,
    });
    const curStatus = summary.currentStatus || app.placementStatus || 'Pending Placement';
    if (!filters.placementStatus.some(ps => ps.toLowerCase() === curStatus.toLowerCase())) {
      return false;
    }
  }

  // 10. Creation Date Range
  if (filters.createdFrom || filters.createdTo) {
    const createdAtTime = new Date(app.createdAt).getTime();
    if (isNaN(createdAtTime)) return false;
    if (filters.createdFrom) {
      const fromTime = new Date(filters.createdFrom).getTime();
      if (!isNaN(fromTime) && createdAtTime < fromTime) return false;
    }
    if (filters.createdTo) {
      const toStr = filters.createdTo.includes('T') ? filters.createdTo : `${filters.createdTo}T23:59:59.999`;
      const toTime = new Date(toStr).getTime();
      if (!isNaN(toTime) && createdAtTime > toTime) return false;
    }
  }

  // 11. Submitted Date Range
  if (filters.submittedFrom || filters.submittedTo) {
    // No createdAt fallback: a record that was never submitted has no
    // submitted date and must not match a submitted-date range.
    const subDate = app.submittedAt || app.submissionDate || app.submittedDate;
    const subTime = new Date(subDate).getTime();
    if (isNaN(subTime)) return false;
    if (filters.submittedFrom) {
      const fromTime = new Date(filters.submittedFrom).getTime();
      if (!isNaN(fromTime) && subTime < fromTime) return false;
    }
    if (filters.submittedTo) {
      const toStr = filters.submittedTo.includes('T') ? filters.submittedTo : `${filters.submittedTo}T23:59:59.999`;
      const toTime = new Date(toStr).getTime();
      if (!isNaN(toTime) && subTime > toTime) return false;
    }
  }

  // 12. Program
  if (filters.program && filters.program.length > 0) {
    // Pre-unification semantics: a selected program ID resolves through
    // the programs list to that program's id/code/name set, and the
    // record matches on EXACT (case-insensitive) equality against its
    // programId / programType / program name. Records whose program is
    // stored as a name (e.g. workflow records) match via the resolved
    // name; substring matching is not used, so one program's id can
    // never swallow another's records.
    const appProgId = String(app.programId || '').toLowerCase();
    const appProgType = String(app.programType || app.programName || app.program || '').toLowerCase();
    const targets = new Set<string>();
    filters.program.forEach((prgFilter: string) => {
      const raw = String(prgFilter);
      const v = raw.toLowerCase();
      if (v) targets.add(v);
      const prog = (options?.programsList || []).find((p: any) =>
        String(p.id || '') === raw ||
        String(p.programId || '') === raw ||
        String(p.code || '').toLowerCase() === v ||
        String(p.name || '').toLowerCase() === v
      );
      if (prog) {
        [prog.id, prog.programId, prog.code, prog.shortCode, prog.programCode, prog.name].forEach((x: any) => {
          if (x) targets.add(String(x).toLowerCase());
        });
      }
    });
    const match =
      (appProgId !== '' && targets.has(appProgId)) ||
      (appProgType !== '' && targets.has(appProgType));
    if (!match) return false;
  }

  // 12b. Pricing Variable
  if (filters.pricingVariable && filters.pricingVariable.length > 0) {
    const appVarId = String(app.pricingVariableId || app.pricingVariableSnapshot?.pricingVariableId || '').toLowerCase();
    const appVarCode = String(app.pricingVariableCode || app.pricingVariableSnapshot?.pricingVariableCode || '').toLowerCase();
    const appVarName = String(app.pricingVariableName || app.pricingVariableSnapshot?.pricingVariableName || '').toLowerCase();

    const targets = new Set<string>();
    filters.pricingVariable.forEach((vFilter: string) => {
      const v = String(vFilter).toLowerCase();
      if (v) targets.add(v);
    });

    const match =
      (appVarId !== '' && targets.has(appVarId)) ||
      (appVarCode !== '' && targets.has(appVarCode)) ||
      (appVarName !== '' && targets.has(appVarName));

    if (!match) return false;
  }

  // 13. Term
  if (filters.term && filters.term.length > 0) {
    const appTerm = (app.programTerm || app.programPreferences?.programTerm || app.assignment?.programTerm || '').toLowerCase().trim();
    if (!filters.term.some(t => t.toLowerCase().trim() === appTerm)) {
      return false;
    }
  }

  // 14. Batches (Multi-batch membership evaluation)
  if (filters.batches && filters.batches.length > 0) {
    const appBatchId = String(app.batchId || '');
    const appBatchIds: string[] = Array.isArray(app.batchIds) ? app.batchIds.map(String) : (Array.isArray(app.batches) ? app.batches.map(String) : []);
    const appId = String(app.applicationId || app.id || app.uid || '');

    const isInBatch = filters.batches.some(bFilter => {
      if (appBatchId === bFilter || appBatchIds.includes(bFilter)) return true;
      if (options?.batchesList) {
        const foundBatch = options.batchesList.find((b: any) => String(b.batchId || b.id) === bFilter || String(b.numericId) === bFilter);
        if (foundBatch && Array.isArray(foundBatch.applicantIds) && foundBatch.applicantIds.includes(appId)) {
          return true;
        }
      }
      return false;
    });
    if (!isInBatch) return false;
  }

  // 15. Referrer Assigned
  if (filters.referrer && filters.referrer.length > 0) {
    // Canonical linkage (ApplicantDetail's resolveLinkedReferrer):
    // `referrerId` carries the referrer org's doc id and `referredBy`
    // the referrer's user uid; either may be the stamped shape. Exact,
    // case-insensitive equality against all of them — a record with no
    // referrer value never matches a referrer selection (the old
    // bidirectional includes() matched every unassigned record).
    const candidates = [app.referrerId, app.assignedReferrerId, app.referredBy, app.referrerName, app.referrer]
      .filter((v: any) => (typeof v === 'string' || typeof v === 'number') && String(v).trim() !== '')
      .map((v: any) => String(v).trim().toLowerCase());
    if (candidates.length === 0) return false;
    const match = filters.referrer.some(r => candidates.includes(String(r).trim().toLowerCase()));
    if (!match) return false;
  }

  // 16. Assigned Team Member
  if (filters.assignedTeamMember && filters.assignedTeamMember.length > 0) {
    const assignedIds: string[] = Array.isArray(app.assignedTeamMemberIds) ? app.assignedTeamMemberIds : [];
    const assignedMembers: any[] = Array.isArray(app.assignedTeamMembers) ? app.assignedTeamMembers : [];
    const memberUids = new Set([
      ...assignedIds,
      ...assignedMembers.map(m => m.uid || m.id || m.email),
      app.assignedAgentId,
      app.agentId,
      // Workflow records carry the assignee as ownerUid (the record's
      // owner IS its assigned staff member). Application records do not
      // carry ownerUid, so this changes nothing on the other screens.
      app.ownerUid,
      app.assignment?.teamMemberId,
      app.assignment?.agentId,
    ].filter(Boolean).map(String));

    const match = filters.assignedTeamMember.some(filterUid => {
      if (filterUid === 'Unassigned' && memberUids.size === 0) return true;
      return memberUids.has(filterUid);
    });
    if (!match) return false;
  }

  return true;
}
